import { CommonModule } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { format } from 'date-fns';

import { PersonalEventInterface } from '@app/interface/personal-event';
import { PersonalEventApi } from '@app/services/personal-event-api.service';
import { ToastService } from '@app/services/toast.service';

export interface NewPersonalEventDialogData {
  organizationId: number;
  /** Giorno preselezionato quando si crea dall'interno di una cella. */
  date?: Date;
  /** Se valorizzato il dialog è in modifica invece che in creazione. */
  event?: PersonalEventInterface;
}

/**
 * Creazione e modifica di un'attività della bacheca.
 *
 * Segue il pattern dei dialog recenti dell'app: form template-driven,
 * `panelClass: 'izy-dialog-panel'` impostata da chi apre, e chiusura con
 * 'success' per segnalare al chiamante che deve ricaricare.
 */
@Component({
  selector: 'izy-new-personal-event-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: { dateInput: 'DD/MM/YYYY' },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
  templateUrl: './new-personal-event-dialog.component.html',
  styleUrl: './new-personal-event-dialog.component.scss',
})
export class NewPersonalEventDialogComponent {
  private toast = inject(ToastService);
  private personalEventApi = inject(PersonalEventApi);

  readonly isEdit: boolean;
  saving = false;

  /** `date` resta un Date perché il datepicker lavora su oggetti, non stringhe. */
  model: {
    title: string;
    sub_title: string;
    notes: string;
    date: Date;
    is_important: boolean;
  };

  constructor(
    public dialogRef: MatDialogRef<NewPersonalEventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NewPersonalEventDialogData,
  ) {
    this.isEdit = !!data.event;

    const existing = data.event;
    this.model = {
      title: existing?.title ?? '',
      sub_title: existing?.sub_title ?? '',
      notes: existing?.notes ?? '',
      date: this.resolveInitialDate(),
      is_important: existing?.is_important ?? false,
    };
  }

  private resolveInitialDate(): Date {
    const raw = this.data.event?.date ?? this.data.date;
    if (!raw) {
      return new Date();
    }
    if (raw instanceof Date) {
      return raw;
    }
    // 'YYYY-MM-DD' interpretato come mezzanotte locale, non UTC.
    const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  submit(form: NgForm): void {
    if (form.invalid || this.saving) {
      return;
    }

    const date = this.formatDateForApi(this.model.date);
    if (date === null) {
      this.toast.error('La data inserita non è valida');
      return;
    }

    this.saving = true;

    const payload: Partial<PersonalEventInterface> = {
      title: this.model.title.trim(),
      sub_title: this.model.sub_title?.trim() ?? '',
      notes: this.model.notes?.trim() ?? '',
      date,
      is_important: this.model.is_important,
      // In modifica lo stato di completamento si cambia dalla bacheca, non da
      // qui: va però rimandato indietro perché la PATCH non lo azzeri.
      is_done: this.data.event?.is_done ?? false,
      is_deleted: false,
      organization_id: this.data.organizationId,
    };

    const request$ = this.data.event
      ? this.personalEventApi.updatePersonalEventDetails(
          this.data.organizationId,
          this.data.event.id,
          payload,
        )
      : this.personalEventApi.createPersonalEvent(
          this.data.organizationId,
          payload,
        );

    request$.subscribe({
      next: () => {
        this.toast.success(
          this.isEdit ? 'Attività aggiornata' : 'Attività aggiunta',
        );
        this.dialogRef.close('success');
      },
      error: (err: unknown) => {
        console.error('Errore salvataggio attività:', err);
        this.saving = false;
        this.toast.error("Non è stato possibile salvare l'attività");
      },
    });
  }

  /**
   * Il datepicker con MomentDateAdapter restituisce un Moment, non un Date:
   * si normalizza passando da `toDate()` quando disponibile.
   */
  /** Restituisce `null` se la data non è valida, invece di salvare oggi al suo posto. */
  private formatDateForApi(value: Date | { toDate?: () => Date }): string | null {
    const asDate =
      value instanceof Date
        ? value
        : typeof value?.toDate === 'function'
          ? value.toDate()
          : new Date(value as unknown as string);

    if (isNaN(asDate.getTime())) {
      return null;
    }
    return format(asDate, 'yyyy-MM-dd');
  }
}
